import { Add, Delete } from "@mui/icons-material";
import { Button, Checkbox, FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, includes, isNil } from "lodash";
import moment from "moment";
import numeral from "numeral";
import React from "react";
import BSInput from "../../../common/js/components/BSInput";

const StyledDashed = styled(Button)({
    border: "2px dashed #2C99D2",
    color: "#2C99D2",
    margin: "10px 0 40px",
    padding: "11px 16px",
    width: "100%",
    justifyContent: "start",
    "&:hover": {
        backgroundColor: "rgba(44, 153, 210, 0.08)",
    },
});

const StyledIconOnly = styled(Button)({
    color: "rgba(0, 0, 0, 0.64)",
    marginLeft: 0,
    padding: 0,
    "&:hover": {
        background: "transparent",
    },
});

const StyledInstallmentNumberContainer = styled("div")({
    display: "flex",
    flex: "1 1 auto",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 0 20px 10px",
    maxWidth: "200px",
});

const StyledInstallmentText = styled("span")({
    color: "rgba(0, 0, 0, 0.54)",
    fontSize: "13px",
    fontWeight: 600,
    marginRight: "0.25vw",
    textTransform: "uppercase",
});

const Installments = (props) => {
    const handleChangeInstallmentAmountAndCalculateSplits = (index, value) => {
        props.handleUpdateArrayElement("installments", index, "amount", value);
        if (!isNil(props.voucher)) {
            props.handleCalculateSplits();
        }
    };

    const calculateTotalCommission = (event) => {
        const [totalCommissionKey, totalKmCommissionKey, outsideBrokerCommissionKey] = [
            "totalCommission",
            "totalKmCommission",
            "outsideBrokerCommission",
        ];
        const targetKey = event.target.id === "totalKmCommission" ? totalKmCommissionKey : outsideBrokerCommissionKey;

        // Update target input
        props.handleInput(targetKey, event.target.value);

        // Calculate total KM commission and outside broker commission
        let [totalKmCommission, outsideBrokerCommission] = [
            parseFloat(numeral(get(props.deal, totalKmCommissionKey, 0)).format("0.00")),
            parseFloat(numeral(get(props.deal, outsideBrokerCommissionKey, 0)).format("0.00")),
        ];

        // Overwrite whichever is the target field
        if (event.target.id === "totalKmCommission")
            totalKmCommission = parseFloat(numeral(event.target.value).format("0.00"));
        if (event.target.id === "outsideBrokerCommission")
            outsideBrokerCommission = parseFloat(numeral(event.target.value).format("0.00"));

        // Calculate and update new total commission
        props.handleInput(totalCommissionKey, numeral(totalKmCommission + outsideBrokerCommission).format("0,000.00"));
    };

    const numInstallments = props.installments ? props.installments.length : null;
    if (numInstallments === 0 && props.disableInputs !== true && !props.voucher && props.displayFirstWhenEmpty) {
        props.handleAddArrayElement("installments", { uniqueId: crypto.randomUUID() });
        props.handleUpdateArrayElement("installments", 0, "dueDate", moment().format("YYYY-MM-DD"));
    }

    const voucherViewContext = !isNil(props.voucher);

    //determine editable installments
    let editableInstallments = [];
    const currentInstallment = get(props.voucher, "installmentNum", null);
    const totalInstallments = get(props, "installments.length", 0);
    let upperBound = totalInstallments;
    let lowerBound = 1;
    if (voucherViewContext || (!voucherViewContext && !props.disableInputs)) {
        if (props.onlyEditCurrentInstallment) {
            lowerBound = currentInstallment;
            upperBound = currentInstallment;
        } else if (props.onlyEditCurrentAndFuture) {
            lowerBound = currentInstallment;
        }
        let i = lowerBound;
        while (i <= upperBound) {
            editableInstallments.push(i);
            i++;
        }
    }

    const {
        deal,
        disableInputs,
        displaySellerLandlordCompData,
        errors,
        handleInput,
        installments,
        handleAddArrayElement,
        handleBlurValidate,
        handleDeleteArrayElement,
        handleUpdateArrayElement,
        onlyEditCurrentInstallment,
        onlyEditCurrentAndFuture,
        preventAddSubtract,
        isConsultingDeal,
        voucher,
        handleVoucherChangeAndCalculateSplits,
        isVoucher,
    } = props;

    const isAccounting = props.userProfile.role === "accounting";
    const commissionInputsDisabled = (isVoucher && !isAccounting) || disableInputs;
    const installmentsErrors =
        isConsultingDeal || isVoucher ? (errors?.deal?.installments ?? {}) : (errors?.installments ?? {});
    const totalKmCommissionError =
        isConsultingDeal || isVoucher ? errors?.deal?.totalKmCommission : errors?.totalKmCommission;

    return (
        <div>
            {(isConsultingDeal ||
                isVoucher ||
                (displaySellerLandlordCompData &&
                    !includes(["Outside", "Outside Draft", "Appraisal", "Appraisal Draft"], deal.compStatus))) && (
                <React.Fragment>
                    <div
                        className="commission-container"
                        style={{ display: "flex", flexWrap: "wrap", margin: "0 -8px" }}
                    >
                        <div
                            className="commission-item"
                            style={{ flex: "0 0 50%", padding: "0 8px", boxSizing: "border-box" }}
                        >
                            <BSInput
                                id="totalKmCommission"
                                label="Total Commission Due To KM Only*"
                                helperText="Required (ex. 981.01)"
                                errorText="Required (ex. 981.01)"
                                error={totalKmCommissionError}
                                value={deal.totalKmCommission}
                                onChange={
                                    voucher
                                        ? (e) => handleVoucherChangeAndCalculateSplits(true, e)
                                        : calculateTotalCommission
                                }
                                placeholder="Enter amount"
                                startAdornment="$"
                                disabled={commissionInputsDisabled}
                                numeric
                                onBlurValidate={(id, value) =>
                                    handleBlurValidate(isVoucher ? null : deal.transactionType, id, value)
                                }
                            />
                        </div>
                        <div
                            className="commission-item"
                            style={{ flex: "0 0 50%", padding: "0 8px", boxSizing: "border-box" }}
                        >
                            <BSInput
                                id="outsideBrokerCommission"
                                label="Total Commission Due To Outside Broker"
                                helperText="(ex. 981.01)"
                                persistHelperText
                                errorText="Required (ex. 981.01)"
                                error={errors.outsideBrokerCommission}
                                value={deal.outsideBrokerCommission}
                                onChange={
                                    voucher
                                        ? (e) => handleVoucherChangeAndCalculateSplits(false, e)
                                        : calculateTotalCommission
                                }
                                placeholder="Enter amount"
                                startAdornment="$"
                                disabled={commissionInputsDisabled}
                                numeric
                                onBlurValidate={(id, value) =>
                                    handleBlurValidate(isVoucher ? null : deal.transactionType, id, value)
                                }
                            />
                        </div>
                    </div>
                    <FormControlLabel
                        style={{ marginBottom: "20px" }}
                        control={
                            <Checkbox
                                checked={deal?.outsideBrokerPaidDirectlyFlg ?? false}
                                onChange={(e) =>
                                    handleInput(
                                        isConsultingDeal || isVoucher
                                            ? "deal.outsideBrokerPaidDirectlyFlg"
                                            : "outsideBrokerPaidDirectlyFlg",
                                        e.target.checked
                                    )
                                }
                                value="outsideBrokerPaidDirectlyFlg"
                                disabled={commissionInputsDisabled}
                                color="primary"
                            />
                        }
                        label="Outside Broker is Paid Direct"
                    />
                </React.Fragment>
            )}

            {installments &&
                installments.map((installment, i) => {
                    const totalInstallments = installments.length;
                    const showDeleteButton =
                        i !== 0 &&
                        (preventAddSubtract !== true ||
                            (currentInstallment && installment.installmentNumberDisplay > currentInstallment) ||
                            isNil(installment.installmentKey));
                    return (
                        <div key={installment.uniqueId ?? i}>
                            <div
                                className="installment-row"
                                style={{ display: "flex", flexWrap: "wrap", margin: "0 -8px" }}
                            >
                                <div
                                    className="installment-amount"
                                    style={{ flex: "0 0 41.67%", padding: "0 8px", boxSizing: "border-box" }}
                                >
                                    <BSInput
                                        id="amount"
                                        label="Total Amount of This Payment*"
                                        errorText="Required (ex. 980.01)"
                                        helperText="Required (ex. 981.01)"
                                        value={installment?.amount}
                                        error={get(installmentsErrors, `${installment.uniqueId}.amount`, null)}
                                        onChange={(e) =>
                                            handleChangeInstallmentAmountAndCalculateSplits(i, e.target.value)
                                        }
                                        placeholder="Enter Amount"
                                        startAdornment="$"
                                        disabled={
                                            (disableInputs && !voucherViewContext) ||
                                            ((onlyEditCurrentInstallment || onlyEditCurrentAndFuture) &&
                                                !includes(editableInstallments, installment.installmentNumberDisplay) &&
                                                !isNil(installment.installmentKey))
                                        }
                                        numeric={true}
                                        onBlurValidate={(id, value) => handleBlurValidate("installment", `installments.${installment.uniqueId}.amount`, value)}
                                    />
                                </div>
                                <div
                                    className="installment-date"
                                    style={{ flex: "0 0 33.33%", padding: "0 8px", boxSizing: "border-box" }}
                                >
                                    <BSInput
                                        id="dueDate"
                                        label="Due Date"
                                        value={installment?.dueDate}
                                        error={get(installmentsErrors, `${installment.uniqueId}.dueDate`, null)}
                                        onChange={(e) =>
                                            handleUpdateArrayElement("installments", i, "dueDate", e.target.value)
                                        }
                                        helperText="Required"
                                        errorText="Required (mm/dd/yyyy)"
                                        disabled={
                                            (disableInputs && !voucherViewContext) ||
                                            ((onlyEditCurrentInstallment || onlyEditCurrentAndFuture) &&
                                                !includes(editableInstallments, installment.installmentNumberDisplay) &&
                                                !isNil(installment.installmentKey))
                                        }
                                        type="date"
                                        onBlurValidate={(id, value) => handleBlurValidate("installment", `installments.${installment.uniqueId}.dueDate`, value)}
                                    />
                                </div>

                                <StyledInstallmentNumberContainer>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <StyledInstallmentText>Installment </StyledInstallmentText>
                                        <span style={{ fontSize: "13px", fontWeight: 800 }}>
                                            {i + 1 + " of " + totalInstallments}
                                        </span>
                                    </div>
                                    {showDeleteButton && (
                                        <StyledIconOnly
                                            style={{ minWidth: "fit-content" }}
                                            onClick={() => handleDeleteArrayElement("installments", i)}
                                        >
                                            <Delete style={{ fontSize: "23px" }} />
                                        </StyledIconOnly>
                                    )}
                                </StyledInstallmentNumberContainer>
                            </div>
                        </div>
                    );
                })}
            {((voucherViewContext && !isConsultingDeal) || (!voucherViewContext && preventAddSubtract !== true)) && (
                <StyledDashed onClick={() => handleAddArrayElement("installments", { uniqueId: crypto.randomUUID() })}>
                    <Add />
                    Add Installment
                </StyledDashed>
            )}
        </div>
    );
};

export default Installments;
